import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useIntl } from '@edx/frontend-platform/i18n';
import {
  FormAutosuggest,
  FormAutosuggestOption,
  FormControlFeedback,
} from '@openedx/paragon';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { clearRegistrationBackendError } from '../../data/actions';
import messages from '../../messages';

const JOB_VALUE_KEY = 'value';
const JOB_LABEL_KEY = 'label';

const DEFAULT_JOB_LIST = [
  { value: 'medical_doctor', label: 'MEDICAL DOCTOR' },
  { value: 'nurse', label: 'NURSE' },
  { value: 'pharmacist', label: 'PHARMACIST' },
  { value: 'midwife', label: 'MIDWIFE' },
  { value: 'health_facility_manager', label: 'HEALTH FACILITY MANAGER' },
  { value: 'public_health_specialist', label: 'PUBLIC HEALTH SPECIALIST' },
  { value: 'community_health_worker', label: 'COMMUNITY HEALTH WORKER' },
  { value: 'student_resident', label: 'STUDENT/RESIDENT' },
  { value: 'other', label: 'OTHER' },
];

const JobField = (props) => {
  const {
    name,
    value,
    handleChange,
    handleErrorChange,
    errorMessage,
    floatingLabel,
    jobList,
  } = props;

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const jobs = jobList || DEFAULT_JOB_LIST;
  const [inputText, setInputText] = useState('');

  const selected = jobs.find(
    item => item[JOB_VALUE_KEY] === value,
  ) || { value: '', label: '' };

  useEffect(() => {
    if (value) {
      setInputText(selected[JOB_LABEL_KEY] || '');
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldValue = {
    userProvidedText: inputText,
    selectionValue: selected[JOB_LABEL_KEY] || '',
    selectionId: selected[JOB_VALUE_KEY] || '',
  };

  const requiredError = formatMessage(messages['empty.job_title.field.error']);

  const runValidation = (selectedCode) => {
    const trimmed = (selectedCode || '').trim();
    if (!trimmed) {
      handleErrorChange(name, requiredError);
    } else {
      handleErrorChange(name, '');
    }
  };

  const handleOnFocus = () => {
    dispatch(clearRegistrationBackendError(name));
    handleErrorChange(name, '');
  };

  const handleOnBlur = () => {
    runValidation(value);
  };

  const handleOnChangeAutosuggest = (autosuggestValue) => {
    const code = autosuggestValue.selectionId || '';
    setInputText(autosuggestValue.userProvidedText || autosuggestValue.selectionValue || '');

    handleChange({
      target: {
        name,
        value: code,
        source: 'job_select',
      },
    });

    // Show "No results found" while typing; enforce required validation on blur/submit.
    handleErrorChange(name, '');
  };

  const getOptions = () => jobs.map(option => (
    <FormAutosuggestOption
      key={option[JOB_VALUE_KEY]}
      id={option[JOB_VALUE_KEY]}
    >
      {option[JOB_LABEL_KEY]}
    </FormAutosuggestOption>
  ));
  const hasMatchingOptions = !inputText.trim()
    || jobs.some(option => option[JOB_LABEL_KEY].toLowerCase().includes(inputText.toLowerCase()));

  return (
    <div className="mb-4">
      <FormAutosuggest
        floatingLabel={floatingLabel || formatMessage(messages['registration.job_title.label'])}
        aria-label="job title"
        name={name}
        value={fieldValue || {}}
        className={classNames({ 'form-field-error': errorMessage })}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onChange={handleOnChangeAutosuggest}
      >
        {getOptions()}
      </FormAutosuggest>
      {errorMessage && (
        <FormControlFeedback
          key="error"
          className="form-text-size"
          hasIcon={false}
          feedback-for={name}
          type="invalid"
        >
          {errorMessage}
        </FormControlFeedback>
      )}
      {!errorMessage && !hasMatchingOptions && (
        <div className="autosuggest-no-results">{formatMessage(messages['registration.no.results.found'])}</div>
      )}
    </div>
  );
};

JobField.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleErrorChange: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  floatingLabel: PropTypes.string,
  jobList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
};

JobField.defaultProps = {
  errorMessage: '',
  floatingLabel: '',
  jobList: null,
};

export default JobField;
